import React, { useState, useEffect } from "react";
import AddExpense from "./AddExpense";
import { useAuth } from "react-oidc-context";
import axios from "axios";
import "./App.css";
import Categorize from "./Categorize";
import Topbar from "./Topbar";
import StatementUpload from "./StatementUpload";
import YearlySpending from "./YearlySpending";
import Transactions from "./Transactions";

const API_URL = "https://sdi909uolk.execute-api.ap-south-1.amazonaws.com";
const HOUSEHOLD_ID = "sabari-household";

function tagClass(category) {
  const c = category.toLowerCase();
  if (c.includes("grocery")) return "grocery";
  if (c.includes("food")) return "food";
  if (c.includes("uncategorized")) return "uncategorized";
  return "other";
}

function categoryVisual(category) {
  const value = (category || "Uncategorized").toLowerCase();
  if (value.includes("recharge")) return { icon: "📱", color: "#7B61FF" };
  if (value.includes("tea") || value.includes("coffee")) return { icon: "☕", color: "#A76D3E" };
  if (value.includes("fruit") || value.includes("juice")) return { icon: "🥤", color: "#EF8B36" };
  if (value.includes("food")) return { icon: "🍛", color: "#F07257" };
  if (value.includes("petrol") || value.includes("fuel")) return { icon: "⛽", color: "#D6A321" };
  if (value.includes("transport") || value.includes("travel") || value.includes("trip")) return { icon: "🚌", color: "#3F83F8" };
  if (value.includes("milk")) return { icon: "🥛", color: "#56A9E8" };
  if (value.includes("grocery")) return { icon: "🛒", color: "#14B887" };
  if (value.includes("sport")) return { icon: "⚽", color: "#43A047" };
  if (value.includes("guest")) return { icon: "🎁", color: "#D45AA5" };
  if (value.includes("hair")) return { icon: "💇", color: "#A66CE0" };
  if (value.includes("medicine")) return { icon: "💊", color: "#E84D60" };
  if (value.includes("cloth") || value.includes("shopping")) return { icon: "👕", color: "#9A62D8" };
  if (value.includes("electric")) return { icon: "💡", color: "#F0B429" };
  if (value.includes("snack")) return { icon: "🍿", color: "#E97843" };
  if (value.includes("movie")) return { icon: "🎬", color: "#5779D9" };
  if (value.includes("personal")) return { icon: "✨", color: "#A66CE0" };
  return { icon: "🏷️", color: "#8190A5" };
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return { text: "Good morning", quote: "A penny saved is a penny earned." };
  if (hour < 17) return { text: "Good afternoon", quote: "Small steps every day add up." };
  return { text: "Good evening", quote: "Track today, thank yourself tomorrow." };
}

function getUserId(email) {
  if (email === "sabari.lpc@gmail.com") return "sabari";
  if (email === "kirutheka1@gmail.com") return "kirutheka";
  return email;
}

function App() {
  const auth = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [showCategorize, setShowCategorize] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showYearlySpending, setShowYearlySpending] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [view, setView] = useState("mine");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [showGreeting, setShowGreeting] = useState(true);
  const [theme, setTheme] = useState(localStorage.getItem("expense-tracker-theme") || "light");
  const [monthlyBudget, setMonthlyBudget] = useState(() => {
    const savedBudget = Number(localStorage.getItem("expense-tracker-monthly-budget"));
    return Number.isFinite(savedBudget) && savedBudget > 0 ? savedBudget : 30000;
  });
  const [showBudgetEdit, setShowBudgetEdit] = useState(false);

  useEffect(() => {
    document.body.className = theme === "dark" ? "theme-dark" : "";
    localStorage.setItem("expense-tracker-theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("expense-tracker-monthly-budget", String(monthlyBudget));
  }, [monthlyBudget]);

  useEffect(() => {
    const timer = setTimeout(() => setShowGreeting(false), 30000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (auth.isAuthenticated) fetchExpenses();
    // eslint-disable-next-line
  }, [auth.isAuthenticated]);

  const fetchExpenses = async () => {
    setLoadingExpenses(true);
    try {
      const response = await axios.get(`${API_URL}/expenses`, {
        params: { household_id: HOUSEHOLD_ID },
        headers: { Authorization: `Bearer ${auth.user?.id_token}` },
      });
      setExpenses(response.data);
    } catch (err) {
      console.error("Failed to fetch expenses:", err);
    }
    setLoadingExpenses(false);
  };

  const signOut = () => {
    auth.removeUser();
    const clientId = "nl7f6bqitkv875isc7sao6n1f";
    const logoutUri = window.location.origin;
    const cognitoDomain = "https://ap-south-1ngvhgxzhf.auth.ap-south-1.amazoncognito.com";
    window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  if (auth.isLoading) return <div className="loading">Loading...</div>;
  if (auth.error) return <div className="loading">Error: {auth.error.message}</div>;

  if (!auth.isAuthenticated) {
    return (
      <div className="signin-shell">
        <div className="signin-card">
          <div className="signin-icon">₹</div>
          <h1 className="signin-title">Household Expenses</h1>
          <p className="signin-subtitle">Track spending together, effortlessly.</p>
          <button className="btn btn-dark signin-btn" onClick={() => auth.signinRedirect()}>
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Use the same selection here and on the dashboard: My spends or Household.
  const isOwner = auth.user?.profile["cognito:groups"]?.includes("Owner");
  const visibleExpenses = visibleExpensesForPage(expenses, view, auth.user?.profile.email, auth.user?.profile["cognito:groups"]);
  const spendingScope = view === "household" && isOwner ? "Everyone's spending" : "My spending";
  const monthExpenses = visibleExpenses.filter((expense) => isInMonth(expense, selectedMonth));
  const monthLabel = selectedMonth.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  const now = new Date();
  const isCurrentMonth = selectedMonth.getFullYear() === now.getFullYear() && selectedMonth.getMonth() === now.getMonth();
  const changeMonth = (offset) => setSelectedMonth((month) => new Date(month.getFullYear(), month.getMonth() + offset, 1));
  const activeModule = showYearlySpending ? "analysis" : showUpload ? "upload" : showCategorize ? "review" : showAddExpense ? "add" : "dashboard";
  const goTo = (module) => {
    setShowAddExpense(module === "add");
    setShowCategorize(module === "review");
    setShowUpload(module === "upload");
    setShowYearlySpending(module === "analysis");
    setShowTransactions(false);
  };

  if (showCategorize) {
    return (
      <>
        <Topbar theme={theme} setTheme={setTheme} userEmail={auth.user?.profile.email} onSignOut={signOut} />
        <Categorize
          expenses={expenses}
          token={auth.user?.id_token}
          onDone={() => {
            setShowCategorize(false);
            fetchExpenses();
          }}
        />
        <MobileNav activeModule={activeModule} onNavigate={goTo} uncategorizedCount={expenses.filter((expense) => expense.category === "Uncategorized").length} />
      </>
    );
  }

  if (showUpload) {
    return <><Topbar theme={theme} setTheme={setTheme} userEmail={auth.user?.profile.email} onSignOut={signOut} /><StatementUpload token={auth.user?.id_token} onDone={() => setShowUpload(false)} /><MobileNav activeModule={activeModule} onNavigate={goTo} uncategorizedCount={expenses.filter((expense) => expense.category === "Uncategorized").length} /></>;
  }

  if (showYearlySpending) {
    return <><Topbar theme={theme} setTheme={setTheme} userEmail={auth.user?.profile.email} onSignOut={signOut} /><YearlySpending expenses={visibleExpenses} scopeLabel={spendingScope} selectedMonth={selectedMonth} onDone={() => setShowYearlySpending(false)} /><MobileNav activeModule={activeModule} onNavigate={goTo} uncategorizedCount={expenses.filter((expense) => expense.category === "Uncategorized").length} /></>;
  }

  if (showTransactions) {
    return <><Topbar theme={theme} setTheme={setTheme} userEmail={auth.user?.profile.email} onSignOut={signOut} /><Transactions expenses={visibleExpenses} selectedMonth={selectedMonth} onDone={() => setShowTransactions(false)} /><MobileNav activeModule={activeModule} onNavigate={goTo} uncategorizedCount={expenses.filter((expense) => expense.category === "Uncategorized").length} /></>;
  }

  if (showAddExpense) {
    return (
      <>
        <Topbar theme={theme} setTheme={setTheme} userEmail={auth.user?.profile.email} onSignOut={signOut} />
        <AddExpense
          userId={getUserId(auth.user?.profile.email)}
          token={auth.user?.id_token}
          onDone={() => {
            setShowAddExpense(false);
            fetchExpenses();
          }}
        />
        <MobileNav activeModule={activeModule} onNavigate={goTo} uncategorizedCount={expenses.filter((expense) => expense.category === "Uncategorized").length} />
      </>
    );
  }

  const visibleTotal = monthExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);
  const uncategorizedCount = expenses.filter((expense) => expense.category === "Uncategorized").length;
  const budgetProgress = Math.min((visibleTotal / monthlyBudget) * 100, 100);
  const budgetRemaining = Math.max(monthlyBudget - visibleTotal, 0);
  const previousMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1);
  const previousMonthExpenses = visibleExpenses.filter((expense) => isInMonth(expense, previousMonth));
  const previousCategoryTotals = previousMonthExpenses.reduce((totals, expense) => {
    const category = expense.category || "Uncategorized";
    totals[category] = (totals[category] || 0) + parseFloat(expense.amount);
    return totals;
  }, {});
  const [topPreviousCategory, topPreviousAmount] = Object.entries(previousCategoryTotals)
    .sort(([, firstAmount], [, secondAmount]) => secondAmount - firstAmount)[0] || [];
  const previousMonthTotal = previousMonthExpenses.reduce((sum, expense) => sum + parseFloat(expense.amount), 0);
  const previousCategoryShare = previousMonthTotal > 0 ? Math.round((topPreviousAmount / previousMonthTotal) * 100) : 0;

  const categoryTotals = {};
  monthExpenses.forEach((e) => {
    const cat = e.category || "Uncategorized";
    categoryTotals[cat] = (categoryTotals[cat] || 0) + parseFloat(e.amount);
  });
  const sortedCategories = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]);
  const topCategories = sortedCategories.slice(0, 4);
  const otherCategoryTotal = sortedCategories.slice(4).reduce((sum, [, amount]) => sum + amount, 0);
  const donutCategories = otherCategoryTotal > 0 ? [...topCategories, ["Other", otherCategoryTotal]] : topCategories;
  let donutOffset = 0;
  const donutGradient = donutCategories.length ? donutCategories.map(([category, amount]) => {
    const start = donutOffset;
    donutOffset += (amount / visibleTotal) * 100;
    return `${categoryVisual(category).color} ${start}% ${donutOffset}%`;
  }).join(", ") : "#2B3440 0 100%";

  const recent = [...monthExpenses]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);

  return (
    <>
      <Topbar theme={theme} setTheme={setTheme} userEmail={auth.user?.profile.email} onSignOut={signOut} />
      <nav className="app-nav" aria-label="Main navigation">
        <div className="app-nav-inner">
          <button className="nav-action active" type="button">Dashboard</button>
          <button className="nav-action" type="button" onClick={() => goTo("analysis")}>Spending analysis</button>
          <button className="nav-action" type="button" onClick={() => goTo("upload")}>Upload statement</button>
          <button className="nav-action" type="button" onClick={() => goTo("review")}>Review categories{uncategorizedCount ? ` (${uncategorizedCount})` : ""}</button>
          <button className="nav-action nav-action-primary" type="button" onClick={() => goTo("add")}>+ Add expense</button>
        </div>
      </nav>
      <div className="app-shell">
        <div className="app-header">
          <div className="app-header-content">
          <div className="header-summary">
          <div className="app-header-top">
            <h1 className="app-title">Household Expenses</h1>
          </div>
          {showGreeting && (
            <p className="greeting-banner">
              {getGreeting().text}, {auth.user?.profile.email.split("@")[0]} — {getGreeting().quote}
            </p>
          )}
          <div className="month-switcher" aria-label="Select a month">
            <button className="month-arrow" type="button" onClick={() => changeMonth(-1)} aria-label="Previous month">‹</button>
            <div><span>Viewing month</span><strong>{monthLabel}</strong></div>
            <button className="month-arrow" type="button" onClick={() => changeMonth(1)} disabled={isCurrentMonth} aria-label="Next month">›</button>
          </div>
          <div className="total-block">
            <span className="total-label">₹</span>
            <span className="total-amount">{visibleTotal.toFixed(2)}</span>
          </div>

          {isOwner && (
            <div style={{ marginTop: 10 }}>
              <button className={`btn btn-ghost ${view === "mine" ? "btn-selected" : ""}`} onClick={() => setView("mine")}>My spending</button>
              <button className={`btn btn-ghost ${view === "household" ? "btn-selected" : ""}`} onClick={() => setView("household")}>Everyone's spending</button>
            </div>
          )}
          </div>

          <aside className="header-insights" aria-label="Monthly insights">
            <section className="insight-card pulse-card">
              <div className="pulse-heading-row">
                <p className="insight-kicker">Monthly pulse</p>
                {showBudgetEdit ? (
                  <label className="budget-edit">
                    <span className="visually-hidden">Monthly budget amount</span>
                    <input
                      type="number"
                      min="1"
                      step="500"
                      autoFocus
                      value={monthlyBudget}
                      onChange={(event) => setMonthlyBudget(Math.max(1, Number(event.target.value) || 1))}
                      onBlur={() => setShowBudgetEdit(false)}
                    />
                  </label>
                ) : (
                  <button className="budget-edit-button" type="button" onClick={() => setShowBudgetEdit(true)}>Edit</button>
                )}
              </div>
              <div className="pulse-budget-row">
                <div className="budget-ring budget-ring-compact" style={{ "--budget-progress": `${budgetProgress}%` }}>
                  <div><strong>{Math.round(budgetProgress)}%</strong><span>used</span></div>
                </div>
                <div>
                  <p className="pulse-budget-amount">₹{visibleTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })} <span>of ₹{monthlyBudget.toLocaleString("en-IN")}</span></p>
                  <p className={visibleTotal > monthlyBudget ? "pulse-remaining expense-value" : "pulse-remaining remaining-value"}>{visibleTotal > monthlyBudget ? `₹${(visibleTotal - monthlyBudget).toLocaleString("en-IN", { maximumFractionDigits: 0 })} over budget` : `₹${budgetRemaining.toLocaleString("en-IN", { maximumFractionDigits: 0 })} left`}</p>
                </div>
              </div>
              <div className="pulse-divider" />
              <p className="insight-kicker">Last month’s largest spend</p>
              {topPreviousCategory ? (
                <>
                  <p className="pulse-category"><span className="pulse-category-dot" />{topPreviousCategory} <strong>₹{topPreviousAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })} · {previousCategoryShare}%</strong></p>
                  <p className="category-insight-copy">Keep an eye on this category this month.</p>
                </>
              ) : (
                <p className="category-insight-copy">No spending was recorded for {previousMonth.toLocaleDateString("en-IN", { month: "long" })} yet.</p>
              )}
            </section>
          </aside>
          </div>
        </div>

        <div className="section dashboard-columns">
          <div className="category-column">
            <div className="category-section-heading"><div className="section-title">Spending by category</div><button type="button" className="category-analysis-link" onClick={() => goTo("analysis")}>See analysis</button></div>
            {topCategories.length ? (
              <div className="category-summary-card">
                <div className="category-donut-row">
                  <div className="category-donut" style={{ background: `conic-gradient(${donutGradient})` }}><div><strong>₹{visibleTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</strong><span>spent</span></div></div>
                </div>
                <div className="category-summary-list">
                  {topCategories.map(([category, amount]) => {
                    const visual = categoryVisual(category);
                    const share = (amount / visibleTotal) * 100;
                    return <div className="category-summary-row" key={category}>
                      <div className="category-summary-label"><span className="category-emoji">{visual.icon}</span><span>{category}</span></div>
                      <div className="category-summary-value"><strong>₹{amount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</strong><span>{Math.round(share)}%</span></div>
                      <div className="category-progress"><span style={{ width: `${share}%`, backgroundColor: visual.color }} /></div>
                    </div>;
                  })}
                </div>
              </div>
            ) : <div className="empty-state">No category data for {monthLabel}.</div>}
          </div>

          <div className="ledger-column">
            <div className="ledger-section-heading"><div className="section-title">Recent entries</div><button type="button" className="category-analysis-link" onClick={() => setShowTransactions(true)}>See all</button></div>
            {loadingExpenses ? (
              <div className="loading">Loading expenses...</div>
            ) : recent.length === 0 ? (
              <div className="empty-state">No expenses recorded for {monthLabel}.</div>
            ) : (
              <div className="ledger">
                {recent.map((e) => (
                  <div className="entry" key={e.expense_id}>
                    <div className="entry-body">
                      <div className="entry-top">
                        <span className="merchant">{e.merchant_name}</span>
                        <span className="amount">₹{parseFloat(e.amount).toFixed(2)}</span>
                      </div>
                      <div className="entry-meta">
                        <span className={`tag ${tagClass(e.category)}`}>{e.category}</span>
                        <span className="paymethod">{e.payment_method}</span>
                        <span className="date">{e.expense_date}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
      <MobileNav activeModule={activeModule} onNavigate={goTo} uncategorizedCount={uncategorizedCount} />
    </>
  );
}

export default App;

function visibleExpensesForPage(expenses, view, email, groups) {
  const isOwner = groups?.includes("Owner");
  return view === "household" && isOwner ? expenses : expenses.filter((expense) => expense.user_id === getUserId(email));
}

function isInMonth(expense, month) {
  const date = new Date(expense.expense_date || expense.created_at);
  return !Number.isNaN(date.getTime()) && date.getFullYear() === month.getFullYear() && date.getMonth() === month.getMonth();
}

function MobileNav({ activeModule, onNavigate, uncategorizedCount }) {
  const items = [
    { id: "dashboard", label: "Dashboard", icon: "⌂" },
    { id: "analysis", label: "Analysis", icon: "◔" },
    { id: "upload", label: "Upload", icon: "↑" },
    { id: "review", label: "Review", icon: "⌑" },
  ];

  return (
    <nav className="mobile-nav" aria-label="Mobile navigation">
      {items.slice(0, 2).map((item) => <MobileNavItem key={item.id} item={item} activeModule={activeModule} onNavigate={onNavigate} />)}
      <button className={`mobile-nav-add ${activeModule === "add" ? "is-active" : ""}`} type="button" onClick={() => onNavigate("add")} aria-label="Add expense">+</button>
      {items.slice(2).map((item) => <MobileNavItem key={item.id} item={item} activeModule={activeModule} onNavigate={onNavigate} badge={item.id === "review" ? uncategorizedCount : 0} />)}
    </nav>
  );
}

function MobileNavItem({ item, activeModule, onNavigate, badge }) {
  const isActive = activeModule === item.id;
  return <button className={`mobile-nav-item ${isActive ? "is-active" : ""}`} type="button" onClick={() => onNavigate(item.id)} aria-current={isActive ? "page" : undefined}><span className="mobile-nav-icon">{item.icon}{badge > 0 && <b className="mobile-nav-badge">{badge > 99 ? "99+" : badge}</b>}</span>{isActive && <span className="mobile-nav-label">{item.label}</span>}</button>;
}
